import React, { useState, useEffect, useMemo, useRef } from 'react'

import { useDebouncedCallback } from 'use-debounce'
import styled from 'styled-components'
import { useSelector } from 'react-redux'

import {
  useAddRecipientMutation,
  useRecipientQuery,
} from 'src/Redux/wise-process/recipientApi'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import {
  getRequirements,
  getWiseLoading,
} from 'src/Redux/wise-process/selectors'
import {
  useGetRequirements,
  useSpecifyRequirements,
} from 'src/Redux/wise-process/api-actions'

import { PrimaryTabs } from '../PrimaryTabs'

import BankDetailsForm from './BankDetailsForm'
import { WisePayoutSkeleton } from './WisePayoutSkeleton'
import { BankDetails, getInitialValues } from './helpers'
import { UserInfo } from './style'
import { PayoutAlerts } from './PayoutAlerts'
import { CurrencyForm } from './CurrencyForm'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const DELAY = 300
const KEYS_AMOUNT = 2

const WisePayout: React.FunctionComponent = () => {
  const dispatch = useAppDispatch()
  const requirements = useSelector(getRequirements)
  const recipient = useRecipientQuery()
  const [activeTab, setActiveTab] = useState<string>()
  const [currentCurrency, setCurrentCurrency] = useState<string | undefined>(
    () => recipient?.currency,
  )
  const isWiseLoading = useAppSelector(getWiseLoading)
  const [isSubmitSuccessAlert, setIsSubmitSuccessAlert] = useState(false)
  const [isSubmitFailedAlert, setIsSubmitFailedAlert] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [serverFieldErrors, setServerFieldErrors] = useState<
    Record<string, string>
  >({})
  const addTransferwiseRecipient = useAddRecipientMutation()
  const getRequirementsData = useGetRequirements()
  const checkRequirements = useSpecifyRequirements()

  const containerRef = React.useRef<HTMLDivElement>(null)
  const hasInitializedCurrencyRef = useRef(false)

  const [prevRecipientForCurrency, setPrevRecipientForCurrency] =
    useState(recipient)
  if (prevRecipientForCurrency !== recipient && recipient && !currentCurrency) {
    setPrevRecipientForCurrency(recipient)
    setCurrentCurrency(recipient.currency)
  }

  useEffect(() => {
    if (recipient && !hasInitializedCurrencyRef.current) {
      hasInitializedCurrencyRef.current = true
      void dispatch(getRequirementsData(recipient.currency))
    }
  }, [recipient, dispatch, getRequirementsData])

  const [prevRequirements, setPrevRequirements] = useState(requirements)
  const [prevRecipientForTab, setPrevRecipientForTab] = useState(recipient)

  if (prevRequirements !== requirements || prevRecipientForTab !== recipient) {
    setPrevRequirements(requirements)
    setPrevRecipientForTab(recipient)
    if (requirements?.length) {
      setActiveTab((prev) => {
        if (prev && requirements.some((item) => item.title === prev)) {
          return prev
        }
        if (recipient) {
          return requirements.find((item) => item.type === recipient.type)
            ?.title
        }
        return requirements[0].title
      })
    }
  }

  const initialValues = useMemo(
    () => getInitialValues(recipient, requirements),
    [recipient, requirements],
  )

  const handleSubmit = (
    bankDetailsForm: BankDetails,
    transferType: string,
  ): void => {
    setServerFieldErrors({})
    addTransferwiseRecipient({
      currency: currentCurrency || '',
      type: transferType || '',
      details: bankDetailsForm,
    })
      .then((res) => {
        setIsSubmitFailedAlert(false)
        setIsSubmitSuccessAlert(true)
        return res
      })
      .catch((error: unknown) => {
        const errors = (
          error as {
            data?: { errors?: Array<{ message: string; path?: string }> }
          }
        )?.data?.errors
        const fieldErrors: Record<string, string> = {}
        errors?.forEach(({ path, message }) => {
          if (path) {
            fieldErrors[path] = message
          }
        })
        setServerFieldErrors(fieldErrors)
        setErrorMessage(
          errors?.[0]?.message ||
            'Something went wrong. Please contact support team.',
        )
        setIsSubmitFailedAlert(true)
        setIsSubmitSuccessAlert(false)
      })
      .finally(() => {
        containerRef.current?.scrollIntoView({ behavior: 'smooth' })
      })
  }

  const handleSpecifyRequirements = (
    val: string,
    key: string,
    transferType: string,
  ): void => {
    const details: Record<string, string | Record<string, string>> = {}
    const path = key.split('.')
    if (path.length === KEYS_AMOUNT) {
      const [outerPath, nestedPath] = path
      const nesting = {} as Record<string, string>
      nesting[nestedPath] = val
      details[outerPath] = nesting
    } else {
      details[key] = val
    }
    if (currentCurrency && transferType) {
      void dispatch(
        checkRequirements({
          currency: currentCurrency,
          type: transferType,
          details,
        }),
      )
        .unwrap()
        .catch(() => {
          setIsSubmitFailedAlert(true)
          setErrorMessage('Something went wrong')
        })
    }
  }
  const debounceSubmit = useDebouncedCallback(handleSubmit, DELAY)

  const items = requirements
    ?.filter(({ type }) => type !== 'email')
    ?.map((tab) => ({
      label: tab.title,
      key: tab.title,
      children: (
        <>
          {tab.usageInfo && <UserInfo>{tab.usageInfo}</UserInfo>}
          <BankDetailsForm
            item={tab}
            serverErrors={serverFieldErrors}
            initialValues={
              recipient &&
              recipient.currency === currentCurrency &&
              recipient.type === tab.type
                ? initialValues
                : {}
            }
            handleSpecifyRequirements={(val, key) =>
              handleSpecifyRequirements(val, key, tab.type)
            }
            onSubmit={(data) => {
              debounceSubmit(data, tab.type)
            }}
          />
        </>
      ),
    }))

  const showLoader = isWiseLoading && !requirements?.length

  return (
    <Container ref={containerRef}>
      <PayoutAlerts
        errorMessage={errorMessage}
        isSubmitFailedAlert={isSubmitFailedAlert}
        isSubmitSuccessAlert={isSubmitSuccessAlert}
        setIsSubmitFailedAlert={setIsSubmitFailedAlert}
        setIsSubmitSuccessAlert={setIsSubmitSuccessAlert}
      />

      <CurrencyForm setCurrentCurrency={setCurrentCurrency} />
      {showLoader ? (
        <WisePayoutSkeleton />
      ) : (
        <PrimaryTabs
          activeKey={activeTab}
          items={items || []}
          onChange={(activeKey: string) => {
            setServerFieldErrors({})
            setIsSubmitFailedAlert(false)
            setActiveTab(activeKey)
          }}
        />
      )}
    </Container>
  )
}
export default WisePayout
